/**
 * DexieRepo — IndexedDB implementation of {@link Repo} for the web/PWA build.
 * (The Capacitor native build will provide a SQLite-backed Repo with the same
 * contract; the shared conformance suite keeps them in lockstep.)
 */
import Dexie, { type Table } from "dexie";
import type {
  Inspection,
  QuestionAnswer,
  QuestionAspectResponse,
  InspectionHumanRankResponse,
  PhotoComparison,
  NegativeObservation,
  Rectification,
  Rank,
  DeficiencyFindingWithCodes,
  DeficiencyFindingListItem,
} from "@/types/api";
import type { Repo } from "../repo";
import { assembleAnswerDetail, synthesizeQueuedNoms, withInspectionMeta } from "../overlay";
import type {
  OutboxMutation,
  OutboxAttachment,
  OutboxState,
  PullDelta,
  AnswerDetail,
} from "../types";

const ACTIVE: OutboxState[] = ["pending", "syncing", "failed"];

class IcheckOfflineDB extends Dexie {
  inspections!: Table<Inspection, number>;
  answers!: Table<QuestionAnswer, number>;
  aspects!: Table<QuestionAspectResponse, number>;
  humanRank!: Table<InspectionHumanRankResponse, number>;
  photo!: Table<PhotoComparison, number>;
  noms!: Table<NegativeObservation, number>;
  rectifications!: Table<Rectification, number>;
  ranks!: Table<Rank, number>;
  outbox!: Table<OutboxMutation, number>;
  attachments!: Table<OutboxAttachment, number>;
  meta!: Table<{ key: string; value: string }, string>;
  kv!: Table<{ key: string; value: unknown }, string>;
  assets!: Table<{ id: number; blob: Blob }, number>;

  constructor(name = "icheck-offline") {
    super(name);
    this.version(1).stores({
      inspections: "id, vesselId",
      answers: "id, inspectionId, [inspectionId+questionId]",
      aspects: "id, answerId, [answerId+aspect]",
      humanRank: "id, answerId, [answerId+rankId]",
      photo: "id, answerId",
      noms: "id, inspectionId",
      rectifications: "id, negativeObservationId",
      ranks: "id",
      outbox: "++id, coalesceKey, state, [inspectionId+questionId]",
      attachments: "++id, state",
      meta: "key",
      kv: "key",
      assets: "id",
    });
  }
}

const CURSOR_KEY = "cursor";
const SEQ_KEY = "seq";

export class DexieRepo implements Repo {
  private db: IcheckOfflineDB;
  constructor(name?: string) {
    this.db = new IcheckOfflineDB(name);
  }

  /* ── cache reads ─────────────────────────────────────────────── */
  getInspection(id: number) { return this.db.inspections.get(id); }
  listInspections() { return this.db.inspections.toArray(); }
  listAnswers(inspectionId: number) { return this.db.answers.where("inspectionId").equals(inspectionId).toArray(); }
  listNegativeObservations(inspectionId: number) { return this.db.noms.where("inspectionId").equals(inspectionId).toArray(); }
  listAllNegativeObservations() { return this.db.noms.toArray(); }
  async listQueuedNegativeObservations(inspectionId: number): Promise<NegativeObservation[]> {
    const queued = (await this.db.outbox.where("state").anyOf(ACTIVE).toArray()).filter((m) => m.entity === "nom");
    if (queued.length === 0) return [];
    const answers = await this.db.answers.where("inspectionId").equals(inspectionId).toArray();
    const envByQ = new Map(answers.map((a) => [a.questionId, a.id]));
    return synthesizeQueuedNoms(queued, (_insp, q) => envByQ.get(q), inspectionId);
  }
  async listAllQueuedNegativeObservations(): Promise<NegativeObservation[]> {
    const queued = (await this.db.outbox.where("state").anyOf(ACTIVE).toArray()).filter((m) => m.entity === "nom");
    if (queued.length === 0) return [];
    const answers = await this.db.answers.toArray();
    const envByKey = new Map(answers.map((a) => [`${a.inspectionId}:${a.questionId}`, a.id]));
    const synth = synthesizeQueuedNoms(queued, (insp, q) => envByKey.get(`${insp}:${q}`));
    // Stamp vessel/type from the cached inspection so the global Observations
    // list groups/filters/displays the offline NOM like a synced one.
    const inspById = new Map((await this.db.inspections.toArray()).map((i) => [i.id, i]));
    return synth.map((n) => withInspectionMeta(n, inspById.get(n.inspectionId)));
  }

  async listAllQueuedFindings(): Promise<DeficiencyFindingListItem[]> {
    const rows = await this.db.kv.where("key").startsWith("findings:").toArray();
    if (rows.length === 0) return [];
    const inspById = new Map((await this.db.inspections.toArray()).map((i) => [i.id, i]));
    const out: DeficiencyFindingListItem[] = [];
    for (const r of rows) {
      for (const fwc of ((r.value as DeficiencyFindingWithCodes[] | undefined) ?? [])) {
        // Only offline-created (temp negative id) findings — synced ones already
        // come from `deficiency-findings:all`.
        if (!fwc?.finding || fwc.finding.id >= 0) continue;
        const insp = inspById.get(fwc.finding.inspectionId);
        out.push({
          ...fwc,
          inspectionType: insp?.type ?? null,
          inspectionName: insp?.name ?? null,
          vesselId: insp?.vesselId ?? null,
          // The cached Inspection row carries only vesselId; the list resolves
          // the name/imo from its vessels cache where needed.
          vesselName: null,
          vesselImo: null,
        });
      }
    }
    return out;
  }
  async evictQueuedFinding(tempId: number): Promise<void> {
    const rows = await this.db.kv.where("key").startsWith("findings:").toArray();
    for (const r of rows) {
      const arr = (r.value as DeficiencyFindingWithCodes[] | undefined) ?? [];
      const next = arr.filter((fwc) => fwc?.finding?.id !== tempId);
      if (next.length !== arr.length) await this.db.kv.put({ key: r.key, value: next });
    }
    await this.db.kv.bulkDelete([`deficiency-finding:${tempId}`, `rectifications:by-finding:${tempId}`]);
  }
  listRectifications() { return this.db.rectifications.toArray(); }
  getRanks() { return this.db.ranks.toArray(); }

  async getAnswerDetail(inspectionId: number, questionId: number): Promise<AnswerDetail> {
    const cachedEnvelope = await this.db.answers.where("[inspectionId+questionId]").equals([inspectionId, questionId]).first();
    const answerId = cachedEnvelope?.id ?? null;
    const [cachedAspects, cachedHuman, cachedPhoto] = await Promise.all([
      answerId != null ? this.db.aspects.where("answerId").equals(answerId).toArray() : Promise.resolve([]),
      answerId != null ? this.db.humanRank.where("answerId").equals(answerId).toArray() : Promise.resolve([]),
      answerId != null ? this.db.photo.where("answerId").equals(answerId).toArray() : Promise.resolve([]),
    ]);
    const pending = await this.db.outbox.where("[inspectionId+questionId]").equals([inspectionId, questionId]).toArray();
    return assembleAnswerDetail({ inspectionId, questionId, answerId, cachedEnvelope: cachedEnvelope ?? null, cachedAspects, cachedHuman, cachedPhoto, pending });
  }

  /* ── cache writes ────────────────────────────────────────────── */
  async applyDelta(d: PullDelta): Promise<void> {
    await this.db.transaction("rw", [this.db.inspections, this.db.answers, this.db.aspects, this.db.humanRank, this.db.photo, this.db.noms, this.db.rectifications, this.db.ranks], async () => {
      if (d.inspections?.length) await this.db.inspections.bulkPut(d.inspections);
      if (d.answers?.length) await this.db.answers.bulkPut(d.answers);
      if (d.aspects?.length) await this.db.aspects.bulkPut(d.aspects);
      if (d.humanRankResponses?.length) await this.db.humanRank.bulkPut(d.humanRankResponses);
      if (d.photoComparisons?.length) await this.db.photo.bulkPut(d.photoComparisons);
      if (d.negativeObservations?.length) await this.db.noms.bulkPut(d.negativeObservations);
      if (d.rectifications?.length) await this.db.rectifications.bulkPut(d.rectifications);
      if (d.ranks?.length) await this.db.ranks.bulkPut(d.ranks);
    });
    // Tombstones: evict deleted rows from the mirror + kv cache. Done after the
    // upsert tx (it touches kv + cross-table rows); eviction of an id we never
    // cached is a harmless no-op.
    for (const del of d.deletions ?? []) {
      if (del.entity === "inspection") {
        await this.evictInspection(del.entityId);
        await this.db.kv.bulkDelete([
          `inspection-basic:${del.entityId}`, `inspection-class:${del.entityId}`,
          `inspection-noms:${del.entityId}`, `inspection-opennoms:${del.entityId}`,
          `inspection-response-attachments:${del.entityId}`,
        ]);
      } else if (del.entity === "negativeObservation") {
        await this.db.noms.delete(del.entityId);
        await this.db.rectifications.where("negativeObservationId").equals(del.entityId).delete();
        await this.db.kv.bulkDelete([`observation:${del.entityId}`, `nom-root-causes:${del.entityId}`]);
      } else if (del.entity === "rectification") {
        await this.db.rectifications.delete(del.entityId);
      }
    }
  }

  async evictInspection(inspectionId: number): Promise<void> {
    const answerIds = (await this.db.answers.where("inspectionId").equals(inspectionId).primaryKeys()) as number[];
    const nomIds = (await this.db.noms.where("inspectionId").equals(inspectionId).primaryKeys()) as number[];
    await this.db.transaction("rw", [this.db.inspections, this.db.answers, this.db.aspects, this.db.humanRank, this.db.photo, this.db.noms, this.db.rectifications], async () => {
      await this.db.inspections.delete(inspectionId);
      await this.db.answers.where("inspectionId").equals(inspectionId).delete();
      await this.db.noms.where("inspectionId").equals(inspectionId).delete();
      // Rectifications hang off the inspection's observations — clear them too.
      if (nomIds.length) await this.db.rectifications.where("negativeObservationId").anyOf(nomIds).delete();
      for (const aid of answerIds) {
        await this.db.aspects.where("answerId").equals(aid).delete();
        await this.db.humanRank.where("answerId").equals(aid).delete();
        await this.db.photo.where("answerId").equals(aid).delete();
      }
    });
  }

  async remapInspectionId(tempId: number, serverId: number): Promise<void> {
    const from = String(tempId);
    const to = String(serverId);
    // inspectionId isn't a standalone index on either store, so scan + filter
    // (both tables hold only the small unsynced working set).
    await this.db.transaction("rw", [this.db.outbox, this.db.attachments], async () => {
      const rows = (await this.db.outbox.toArray()).filter((r) => r.inspectionId === tempId);
      for (const r of rows) {
        if (r.id == null) continue;
        const payload = { ...r.payload };
        if (payload.inspectionId === tempId) payload.inspectionId = serverId;
        // tempId is a unique negative integer → safe verbatim substring swap.
        const coalesceKey = r.coalesceKey.split(from).join(to);
        await this.db.outbox.update(r.id, { inspectionId: serverId, coalesceKey, payload });
      }
      const atts = (await this.db.attachments.toArray()).filter((a) => a.inspectionId === tempId);
      for (const a of atts) if (a.id != null) await this.db.attachments.update(a.id, { inspectionId: serverId });
    });
  }

  async remapNegativeObservationId(tempId: number, serverId: number): Promise<void> {
    const from = String(tempId);
    const to = String(serverId);
    await this.db.transaction("rw", [this.db.outbox, this.db.attachments], async () => {
      const rows = (await this.db.outbox.toArray()).filter((r) => r.negativeObservationId === tempId);
      for (const r of rows) {
        if (r.id == null) continue;
        const payload = { ...r.payload };
        if (payload.negativeObservationId === tempId) payload.negativeObservationId = serverId;
        const coalesceKey = r.coalesceKey.split(from).join(to);
        await this.db.outbox.update(r.id, { negativeObservationId: serverId, coalesceKey, payload });
      }
      // Also remap any queued (generic) attachment whose ownerId is this temp NOM
      // — e.g. observation evidence uploaded offline before the NOM synced.
      // Without this the upload stays stuck on "Owner not yet synced" forever
      // (mirrors the equivalent in remapFindingId).
      const atts = (await this.db.attachments.toArray()).filter(
        (a) => a.ownerId === tempId && a.attachmentOwnerType === "negative_observation",
      );
      for (const a of atts) if (a.id != null) await this.db.attachments.update(a.id, { ownerId: serverId });
    });
  }

  async remapFindingId(tempId: number, serverId: number): Promise<void> {
    const from = String(tempId);
    const to = String(serverId);
    // Match by PAYLOAD (deficiencyFindingId / findingId) — portable, and the
    // deficiencyFindingId column isn't a standalone index anyway. Also remap any
    // queued (generic) attachment whose ownerId is this temp finding.
    await this.db.transaction("rw", [this.db.outbox, this.db.attachments], async () => {
      for (const r of await this.db.outbox.toArray()) {
        if (r.id == null) continue;
        const payload = { ...r.payload } as Record<string, unknown>;
        let hit = r.deficiencyFindingId === tempId;
        if (payload.deficiencyFindingId === tempId) { payload.deficiencyFindingId = serverId; hit = true; }
        if (payload.findingId === tempId) { payload.findingId = serverId; hit = true; }
        if (hit) {
          const coalesceKey = r.coalesceKey.split(from).join(to);
          await this.db.outbox.update(r.id, { deficiencyFindingId: serverId, coalesceKey, payload });
        }
      }
      const atts = (await this.db.attachments.toArray()).filter((a) => a.ownerId === tempId);
      for (const a of atts) if (a.id != null) await this.db.attachments.update(a.id, { ownerId: serverId });
    });
  }

  /* ── outbox ──────────────────────────────────────────────────── */
  async enqueue(m: Omit<OutboxMutation, "id" | "state" | "createdAt" | "seq"> & { seq?: number }): Promise<void> {
    await this.db.transaction("rw", [this.db.outbox, this.db.meta], async () => {
      const existing = (await this.db.outbox.where("coalesceKey").equals(m.coalesceKey).toArray()).find((r) => ACTIVE.includes(r.state));
      const seq = m.seq ?? (await this.bumpSeq());
      if (existing && existing.id != null) {
        await this.db.outbox.update(existing.id, { payload: m.payload, clientUpdatedAt: m.clientUpdatedAt, seq, state: "pending", error: null });
      } else {
        await this.db.outbox.add({ ...m, seq, state: "pending", createdAt: new Date().toISOString() });
      }
    });
  }

  async listOutbox(states: OutboxState[] = ACTIVE): Promise<OutboxMutation[]> {
    const rows = await this.db.outbox.where("state").anyOf(states).toArray();
    return rows.sort((a, b) => a.seq - b.seq);
  }

  async markOutbox(ids: number[], state: OutboxState, error: string | null = null): Promise<void> {
    await this.db.transaction("rw", this.db.outbox, async () => {
      for (const id of ids) await this.db.outbox.update(id, { state, error });
    });
  }

  async deleteOutbox(ids: number[]): Promise<void> { await this.db.outbox.bulkDelete(ids); }

  async hasPending(coalesceKey: string): Promise<boolean> {
    const rows = await this.db.outbox.where("coalesceKey").equals(coalesceKey).toArray();
    return rows.some((r) => ACTIVE.includes(r.state));
  }

  /* ── attachments ─────────────────────────────────────────────── */
  async enqueueAttachment(a: Omit<OutboxAttachment, "id" | "state" | "createdAt">): Promise<void> {
    await this.db.attachments.add({ ...a, state: "pending", createdAt: new Date().toISOString() });
  }
  async listAttachments(states: OutboxState[] = ACTIVE): Promise<OutboxAttachment[]> {
    return this.db.attachments.where("state").anyOf(states).toArray();
  }
  async markAttachment(id: number, state: OutboxState, error: string | null = null): Promise<void> {
    await this.db.attachments.update(id, { state, error });
  }
  async deleteAttachment(id: number): Promise<void> { await this.db.attachments.delete(id); }

  /* ── response cache ──────────────────────────────────────────── */
  async kvGet<T>(key: string): Promise<T | undefined> { return (await this.db.kv.get(key))?.value as T | undefined; }
  async kvPut(key: string, value: unknown): Promise<void> { await this.db.kv.put({ key, value }); }
  async kvPutMany(entries: Array<{ key: string; value: unknown }>): Promise<void> {
    if (entries.length) await this.db.kv.bulkPut(entries);
  }
  async kvDeleteByPrefix(prefix: string): Promise<void> { await this.db.kv.where("key").startsWith(prefix).delete(); }

  /* ── binary asset cache ──────────────────────────────────────── */
  async putAsset(attachmentId: number, blob: Blob): Promise<void> { await this.db.assets.put({ id: attachmentId, blob }); }
  async getAsset(attachmentId: number): Promise<Blob | undefined> { return (await this.db.assets.get(attachmentId))?.blob; }

  async deleteCachedAttachment(kvKey: string, attachmentId: number): Promise<void> {
    // ONE transaction: prune the row from the per-owner list kv AND drop the
    // cached bytes — an offline delete can't leave an orphaned blob behind.
    await this.db.transaction("rw", [this.db.kv, this.db.assets], async () => {
      const row = await this.db.kv.get(kvKey);
      if (Array.isArray(row?.value)) {
        const next = (row.value as Array<{ id?: number }>).filter((a) => a?.id !== attachmentId);
        if (next.length !== (row.value as unknown[]).length) await this.db.kv.put({ key: kvKey, value: next });
      }
      await this.db.assets.delete(attachmentId);
    });
  }

  /* ── meta ────────────────────────────────────────────────────── */
  async getCursor(): Promise<string | null> { return (await this.db.meta.get(CURSOR_KEY))?.value ?? null; }
  async setCursor(iso: string): Promise<void> { await this.db.meta.put({ key: CURSOR_KEY, value: iso }); }

  private async bumpSeq(): Promise<number> {
    const row = await this.db.meta.get(SEQ_KEY);
    const next = (row ? Number(row.value) : 0) + 1;
    await this.db.meta.put({ key: SEQ_KEY, value: String(next) });
    return next;
  }
  async nextSeq(): Promise<number> {
    return this.db.transaction("rw", this.db.meta, () => this.bumpSeq());
  }

  async pendingCount(): Promise<number> {
    const [o, a] = await Promise.all([
      this.db.outbox.where("state").anyOf("pending", "failed").count(),
      this.db.attachments.where("state").anyOf("pending", "failed").count(),
    ]);
    return o + a;
  }

  async clear(): Promise<void> {
    await this.db.transaction("rw", this.db.tables, async () => {
      for (const t of this.db.tables) await t.clear();
    });
  }
}
